import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { useContent, useCompany } from '../context/ContentContext';
import { resolveImageUrl } from './ImageUpload';

const features = [
  'High efficiency heat transfer',
  'Custom wattage & sizes available',
  'Corrosion resistant construction',
  'Long service life & durability',
];

export default function ProductSpotlight() {
  const { products } = useContent();
  const companyInfo = useCompany();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || products.length === 0) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % products.length), 4000);
    return () => clearInterval(t);
  }, [paused, products.length]);

  if (products.length === 0) return null;
  const product = products[idx % products.length];
  const go = (delta) => setIdx((i) => (i + delta + products.length) % products.length);

  return (
    <section className="py-14 bg-white" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <p className="font-display tracking-[4px] text-[#f7941e] text-xs mb-2 uppercase">Featured Spotlight</p>
          <h2 className="section-title text-3xl md:text-4xl">Product of the Moment</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-2xl relative">
          <button onClick={() => go(-1)} aria-label="previous" className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-30 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/90 hover:bg-[#f7941e] hover:text-white text-[#1a1a1a] flex items-center justify-center shadow-lg transition-colors">
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <button onClick={() => go(1)} aria-label="next" className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 z-30 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/90 hover:bg-[#f7941e] hover:text-white text-[#1a1a1a] flex items-center justify-center shadow-lg transition-colors">
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          <div className="bg-gradient-to-br from-[#fff7e6] to-[#ffeacb] p-10 flex items-center justify-center relative min-h-[360px]">
            <div className="absolute top-4 left-4 bg-[#f7941e] text-white text-[10px] uppercase tracking-[3px] font-display px-3 py-1 rounded-full z-10">
              {String(idx + 1).padStart(2, '0')} / {String(products.length).padStart(2, '0')}
            </div>
            <img
              key={product.slug}
              src={resolveImageUrl(product.image)}
              alt={product.name}
              className="max-h-72 object-contain drop-shadow-2xl animate-[fadeIn_500ms_ease]"
              style={{ animation: 'fadeIn 500ms ease' }}
            />
          </div>
          <div className="bg-[#1a1a1a] text-white p-10 flex flex-col justify-center min-h-[360px]">
            <p className="font-display tracking-[4px] text-[#f7941e] text-xs mb-2 uppercase">Spotlight</p>
            <h3 key={product.slug} className="font-display text-2xl md:text-3xl uppercase mb-3 leading-tight min-h-[72px]">
              {product.name}
            </h3>
            <div className="w-12 h-1 bg-[#f7941e] mb-5 rounded" />
            <p className="text-gray-300 leading-relaxed mb-5">
              Manufactured with premium materials and rigorously tested in our in-house lab. Engineered for high performance and long service life across industrial heating applications.
            </p>
            <ul className="space-y-2 mb-6">
              {features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-gray-200">
                  <CheckCircle className="w-4 h-4 text-[#f7941e]" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              <Link to={`/products/${product.slug}`} className="btn-brand">
                View Product <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`tel:${companyInfo.phone[0]}`} className="inline-flex items-center gap-2 border border-[#f7941e] text-[#f7941e] font-display px-5 py-2.5 rounded hover:bg-[#f7941e] hover:text-white transition-colors">
                <Phone className="w-4 h-4" /> Enquire
              </a>
            </div>
          </div>
        </div>

        {/* Dots indicator (capped for clean UI) */}
        <div className="flex items-center justify-center gap-1.5 mt-6 flex-wrap max-w-2xl mx-auto">
          {products.map((p, i) => (
            <button
              key={p.slug}
              onClick={() => setIdx(i)}
              aria-label={`Show ${p.name}`}
              className={`h-2 rounded-full transition-all ${i === idx ? 'w-8 bg-[#f7941e]' : 'w-2 bg-[#f1e4c8] hover:bg-[#f7941e]/50'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
